import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { Row, Col, Form, Button, InputGroup, Modal } from "react-bootstrap";
import CurrencyField from "../utils/currencyField";
import { useForm, Controller } from "react-hook-form";
import { bancos } from "../utils/bancos";
import { BiCloudUpload } from "react-icons/bi";
import { http } from "../auth";
import { FiCheck, FiCalendar } from "react-icons/fi";
import { useThemeManager } from "../Hooks/ThemeManager";
import { today } from "../utils/dateUtils";

const customId = "custom-id-yes";

const Reporte2 = ({id_doc, id_servicio, factura, onBankChange, totalAPagar }) => {
  const { themeColors } = useThemeManager();
  const token = localStorage.getItem("token");
  const [selectedCurrency, setSelectedCurrency] = useState("bs");
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(false);
  const [bancosLista, setBancosLista] = useState("");
  const [optionsUSD, setOptionsUSD] = useState([]);
  const [optionsBS, setOptionsBS] = useState([]);
  const inputFileRef = useRef(null);
  const hiddenDateRef = useRef(null);
  const getCoin = localStorage.getItem("selectedCurrency");
  
  useEffect(() => {
    if (getCoin) setSelectedCurrency(getCoin);
  }, []);

  // Efecto para enviar el nombre del banco al padre (solo una vez)
  useEffect(() => {
    const nombreBanco = "transferencia";
    if (onBankChange) {
      onBankChange(nombreBanco);
    }
  }, [onBankChange]);

  /************** CONFIG BANCO ******/
  useEffect(() => {
    http
      .get(`/config/intranet_config_residencial/datos_bancarios`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const bancosBs = response.data?.bs?.bancos?.map((banco) => ({
          config_type: "intranet_clientes_residenciales_datos_bancarios",
          cod_ban: banco?.cod_ban.toLowerCase(),
          nombre: banco?.nombre_banco,
          cuenta: banco?.numero_cuenta,
          tipo: banco?.tipo_cuenta,
          codigo: banco?.cod_ban,
          enable_r: banco?.enabled_r,
          enabled_j: banco?.enabled_j,
          label: banco?.nombre_banco,
          value: banco?.cod_ban,
          image: banco?.image,
          id: banco?.id,
        }));

        const bancosUsd = response?.data?.usd?.map((usd) => ({
          config_type: "intranet_clientes_residenciales_datos_bancarios",
          enabled: true,
          cod_ban: usd.cod_ban,
          nombre: usd.nombre,
          id: usd?.id,
          enable_r: usd?.enabled_r,
          enabled_j: usd?.enabled_j,
        }));

        const resultadoUsd = [...bancosUsd];

        const opcionesUsd = resultadoUsd.map((banco) => ({
          label: banco.nombre,
          value: banco.cod_ban,
          id: banco?.id,
        }));

        setOptionsUSD(opcionesUsd);
        setOptionsBS(bancosBs);
      })
      .catch((error) => {
        console.error("Error al obtener los datos:", error);
      });
  }, [token, getCoin]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const [displayDate, setDisplayDate] = useState(() => {
    const [y, m, d] = today.split('-');
    return `${d}/${m}/${y}`;
  });

  const handleDateChange = (e) => {
    let val = e.target.value.replace(/[^0-9/]/g, '');
    if (val.length === 2 && !val.includes('/')) val += '/';
    if (val.length === 5 && val[2] === '/' && !val.includes('/', 3)) val += '/';
    if (val.length > 10) return;
    setDisplayDate(val);
    const parts = val.split('/');
    if (parts.length === 3) {
      const [d, m, y] = parts;
      if (d.length === 2 && m.length === 2 && y.length === 4) {
        setValue("fecha", `${y}-${m}-${d}`);
      }
    }
  };

  const handleIconClick = () => {
    if (hiddenDateRef.current) {
      hiddenDateRef.current.showPicker?.();
      hiddenDateRef.current.click();
    }
  };

  const handleHiddenDateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const [y, m, d] = val.split('-');
    const formatted = `${d}/${m}/${y}`;
    setDisplayDate(formatted);
    setValue("fecha", val);
  };

  useEffect(() => {
    setValue("fecha", today);
  }, [setValue]);

  // Efecto para actualizar el monto
  useEffect(() => {
    if (totalAPagar) {
      const monto = parseFloat(totalAPagar);
      setValue("monto", monto);
    }
  }, [totalAPagar, setValue]);

  const watchBankBS = watch("banco_destino_bs");
  const watchBankUSD = watch("banco_origen_usd");
  const bancoDestinoIdBs = watch("banco_destino_id");
  const bancoDestinoIdUSD = watch("banco_destino_id_usd");

  // Efecto para inicializar bancos por defecto
  useEffect(() => {
    if (selectedCurrency === "bs" && optionsBS.length > 0 && !watchBankBS) {
      const primerBancoHabilitado = optionsBS.find((banco) => banco.enable_r !== false);
      if (primerBancoHabilitado) {
        setValue("banco_destino_bs", primerBancoHabilitado.value, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
        setValue("banco_destino_id", primerBancoHabilitado.id, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
        setValue("banco_destino", primerBancoHabilitado.value, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }

    if (selectedCurrency === "usd" && optionsUSD.length > 0 && !watchBankUSD) {
      const primerBancoHabilitadoUSD = optionsUSD.find((banco) => banco.enabled !== false);
      if (primerBancoHabilitadoUSD) {
        setValue("banco_origen_usd", primerBancoHabilitadoUSD.value, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
        setValue("banco_destino_id_usd", primerBancoHabilitadoUSD.id, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
        setValue("banco_origen", primerBancoHabilitadoUSD.value, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
        setValue("banco_destino", primerBancoHabilitadoUSD.value, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }
  }, [optionsBS, optionsUSD, selectedCurrency, setValue, watchBankBS, watchBankUSD]);

  // Efecto para sincronizar cuando el usuario cambia el banco manualmente (BS)
  useEffect(() => {
    if (watchBankBS && selectedCurrency === "bs" && optionsBS.length > 0) {
      const selectedOptionBs = optionsBS.find((option) => option.value === watchBankBS);
      if (selectedOptionBs) {
        if (selectedOptionBs.id !== bancoDestinoIdBs) {
          setValue("banco_destino_id", selectedOptionBs?.id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
        setValue("banco_destino", selectedOptionBs?.value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [watchBankBS, selectedCurrency, optionsBS, setValue, bancoDestinoIdBs]);

  // Efecto similar para USD
  useEffect(() => {
    if (watchBankUSD && selectedCurrency === "usd" && optionsUSD.length > 0) {
      const selectedOptionUSD = optionsUSD.find((option) => option.value === watchBankUSD);
      if (selectedOptionUSD) {
        if (selectedOptionUSD.id !== bancoDestinoIdUSD) {
          setValue("banco_destino_id_usd", selectedOptionUSD?.id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
        setValue("banco_origen", selectedOptionUSD?.value, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("banco_destino", selectedOptionUSD?.value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [watchBankUSD, selectedCurrency, optionsUSD, setValue, bancoDestinoIdUSD]);

  const getFormData = (object) =>
    Object.keys(object).reduce((formData, key) => {
      formData.append(key, object[key]);
      return formData;
    }, new FormData());

  const handleFileChange = (e) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    setFile(file);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result.split(",")[1];
    };
    reader.onerror = (error) => {
      console.error("Error al leer el archivo:", error);
    };
    reader.readAsDataURL(file);
  };

  const compressImage = async (file, { quality = 1, type = file.type }) => {
    const imageBitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = imageBitmap.width;
    canvas.height = imageBitmap.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imageBitmap, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, quality));
    return new File([blob], file.name, {
      type: blob.type,
    });
  };

  useEffect(() => {
    localStorage.setItem("selectedCurrency", selectedCurrency);
  }, [selectedCurrency]);

  const onSubmit = async (data) => {

    const submitData = {};

    submitData.monto = data.monto;
    submitData.ref = data.ref;
    submitData.fecha = data.fecha;

    if (selectedCurrency === "bs") {
      submitData.id = bancoDestinoIdBs;
      submitData.banco_origen = data.banco_origen;
      submitData.banco_destino = data.banco_destino;
    } else if (selectedCurrency === "usd") {
      submitData.id = bancoDestinoIdUSD;
      submitData.banco_origen = data.banco_origen;
      submitData.banco_destino = data.banco_origen;
    }

    if (factura) {
      submitData.id_docs = id_doc;
      submitData.id_servicios = "null";
    } else {
      submitData.id_servicios = id_servicio.map(String).join(",");
      submitData.id_docs = 0;
    }


    setLoading(true);

    if (!data.ref || data.ref.length < 6) {
      toast.warning("Estimado usuario, debe ingresar un número de transacción mayor a 6 caracteres", {
        toastId: customId,
        position: "top-center",
      });
      setLoading(false);
      return;
    }

    if (!data.banco_origen || data.banco_origen.length < 4) {
      toast.warning("Estimado usuario, debe seleccionar el banco origen", {
        toastId: customId,
        position: "top-center",
      });
      setLoading(false);
      return;
    }

    if (selectedCurrency === "bs") {
      if (!data.banco_destino || data.banco_destino.length < 4) {
        toast.warning("Estimado usuario, debe seleccionar el banco destino", {
          toastId: customId,
          position: "top-center",
        });
        setLoading(false);
        return;
      }
    }

    if (!data.monto || data.monto < 1) {
      toast.warning("Estimado usuario, debe ingresar un monto a cancelar mayor que cero", {
        toastId: customId,
        position: "top-center",
      });
      setLoading(false);
      return;
    }

    if (!file) {
      toast.warning("Estimado usuario, debe subir una imagen del comprobante de pago", {
        toastId: customId,
        position: "top-center",
      });
      setLoading(false);
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.warning("Estimado usuario, debe subir un archivo que sea en formato JPG, PNG o PDF.", {
        toastId: customId,
        position: "top-center",
      });
      setLoading(false);
      return;
    }

    const convertFileToBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });

    try {
      const base64File = await convertFileToBase64(file);

      const jsonData = {
        ...submitData,
        file: base64File,
        mimetype: file.type,
        moneda: getCoin,
      };


      const response = await http.post(`/intranet-j/transferencia`, jsonData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setLoading(false);
      setStep(true);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        console.error(error.response);
        const messages = {
          400: "Estimado usuario, ya existe un pago reportado en su cuenta con este número de referencia.",
          503: "¡Hola! Gracias por contactarnos. Queríamos informarte que estamos trabajando en mejoras para nuestro portal de pagos, por lo que se encuentra en mantenimiento por el momento. ¡No te preocupes! Estaremos de vuelta en línea pronto y podrás disfrutar de una experiencia de pago aún mejor. ¡Gracias por tu paciencia!",
          422: error.response?.data?.detail?.includes("Datos Invalidos")
            ? "¡Hola! Lamentamos no poder procesar tu pago en este momento. Es necesario que actualices tus datos antes de poder realizar el reporte. Por favor, comunícate con nuestro equipo de atención al cliente a través de Whatsapp al +58 424-3350857 para que te asistan en este proceso."
            : "Parece que alguno de los datos que ingresó están erróneos. Por favor, verifíquelos.",
          500: "Estimado usuario, hubo un error interno en el servidor. Por favor, intente más tarde.",
        };

        const message =
          messages[error?.response?.status] || "Estimado usuario, alguno de los datos que está ingresando es incorrecto.";
        const toastType = [400, 503].includes(error?.response?.status) ? "warning" : "error";

        toast[toastType](message, {
          toastId: customId,
          position: "top-center",
        });
      } else {
        console.error("Error", error.message);
        alert(`Hubo un error interno\nCódigo Error: ${error.name}: ${error.message}`);
      }
    }
  };

  /*******LISTA BANCOS ********/
  useEffect(() => {
    http.get(`/banca/lista`).then((response) => {
      setBancosLista(response.data);
    });
  }, []);

  const RespondPay = () => {
    return (
      <Modal size="md" show={step} onHide={() => setStep(false)} centered className="my-modal no-border">
        <Modal.Body className="text-center p-4  ">
          <div className="center-all">
            <FiCheck className="success-icon" size={120}/>
          </div>
          <h2 className="color-secondary">
            Su reporte de pago <br/> se realizó de forma exitosa
          </h2>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div>
      <h3 className="titulo-report mt-4">Reporte de pagos</h3>
      <span className="steps">Paso 2 de 2</span>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-pago border-card mt-2 p-sm-5">
          <span className="center-all header-bill mt-4">Datos de la transferencia utilizada</span>
          <div className="bank-data mb-2 mb-sm-0">
            <Row className="g-2 g-sm-4">
              <Col xs={12} sm={6}>
                <Controller
                  control={control}
                  name="monto"
                  render={({ field: { onChange, value } }) => {
                    // Si hay un totalAPagar y el valor actual es 0 o null, usar totalAPagar
                    const displayValue = (value === 0 || value === null || value === undefined) && totalAPagar 
                      ? parseFloat(totalAPagar) 
                      : value;
                    
                    
                    return (
                      <Form.Group controlId="monto" className="mb-2">
                        <Form.Label
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            marginBottom: "8px",
                          }}
                        >
                          Monto en {getCoin === "bs" ? "Bs" : "$"}
                        </Form.Label>
                        <CurrencyField
                          className="form-control"
                          onValueChange={(values) => {
                            if (values && values.floatValue !== undefined) {
                              const roundedValue = parseFloat(values.floatValue.toFixed(2));
                              onChange(roundedValue);
                            } else {
                              onChange(null);
                            }
                          }}
                          decimalSeparator={"."}
                          thousandSeparator={true}
                          value={displayValue}
                        />
                      </Form.Group>
                    );
                  }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-2">
                  <Form.Label>N° Referencia del pago</Form.Label>
                  <Form.Control {...register("ref")} type="number" />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-2 g-sm-4">
              <Col xs={12}>
                <Form.Group className="mb-2">
                  <Form.Label className="mobile-label">Documento / Imagen referencial de la transferencia</Form.Label>
                  <InputGroup>
                    <div className="border-btn">
                      <input
                        type="file"
                        ref={inputFileRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        accept=".png,.jpg,.jpeg,.pdf"
                      />
                      <Button
                        onClick={() => inputFileRef.current.click()}
                        variant="outline-secondary"
                        className="btn-archive"
                      >
                        Seleccionar...
                      </Button>
                    </div>
                    <Form.Control className="referencia" placeholder="PNG / JPG o PDF" value={fileName} readOnly />
                    <Button variant="outline-secondary" className="btn-upload" id="button-addon1">
                      <BiCloudUpload className="icon-upload" />
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            {selectedCurrency === "bs" && (
              <Row className="g-2 g-sm-4 mb-2">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Banco de origen</Form.Label>
                    <Form.Select defaultValue="" {...register("banco_origen")}>
                      <option value="" disabled>
                        Seleccione...
                      </option>
                      {bancos.map((banco) => (
                        <option key={banco.value} value={banco.value}>
                          {banco.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}
            <Row className="g-2 g-sm-4">
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>{selectedCurrency === "bs" ? "Banco destino" : "Banco de origen"}</Form.Label>
                  {selectedCurrency === "bs" ? (
                    <>
                      <Form.Select
                        {...register("banco_destino_bs")}
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          const selectedOption = optionsBS.find((option) => option.value === selectedValue);
                          setValue("banco_destino_id", selectedOption?.id);
                          setValue("banco_destino", selectedValue);
                        }}
                      >
                        <option value="" disabled>
                          Seleccione banco destino...
                        </option>
                        {optionsBS
                          .filter((option) => option.enable_r !== false)
                          .map((option, index) => (
                            <option key={index} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </Form.Select>
                    </>
                  ) : (
                    <>
                      <Form.Select
                        {...register("banco_origen_usd")}
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          const selectedOption = optionsUSD.find((option) => option.value === selectedValue);
                          setValue("banco_destino_id_usd", selectedOption?.id);
                          setValue("banco_origen", selectedValue);
                          setValue("banco_destino", selectedValue);
                        }}
                      >
                        <option value="" disabled>
                          Seleccione método de pago en USD
                        </option>
                        {optionsUSD?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha del pago</Form.Label>
                  <div className="input-fecha-wrapper">
                    <Form.Control
                      type="text"
                      id="fecha"
                      className="input-fecha"
                      value={displayDate}
                      onChange={handleDateChange}
                      placeholder="dd/mm/yyyy"
                      maxLength={10}
                    />
                    <input
                      type="date"
                      ref={hiddenDateRef}
                      onChange={handleHiddenDateChange}
                      style={{
                        position: "absolute",
                        width: 0,
                        height: 0,
                        opacity: 0,
                        pointerEvents: "none",
                      }}
                      tabIndex={-1}
                    />
                    <FiCalendar
                      className="input-fecha-icon"
                      onClick={handleIconClick}
                      style={{ cursor: "pointer", pointerEvents: "auto" }}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
        <Button type="submit" style={{ width: "100%" }} className="mt-3 btn-pay">
          {loading ? (
            <div className="center-all">
              <div className="spinner"></div>
            </div>
          ) : (
            <span>Pagar</span>
          )}
        </Button>
      </form>
      <RespondPay />
    </div>
  );
};

export default Reporte2;